import React, { createContext, useContext, useState, useCallback } from 'react';

interface UploadTask {
  id: string;
  filename: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
  metadata?: {
    title: string;
    description?: string;
    tags?: string[];
  };
}

interface UploadContextType {
  uploads: UploadTask[];
  addUpload: (upload: Omit<UploadTask, 'progress' | 'status'>) => void;
  updateUpload: (id: string, updates: Partial<UploadTask>) => void;
  removeUpload: (id: string) => void;
  clearCompleted: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [uploads, setUploads] = useState<UploadTask[]>([]);

  const addUpload = useCallback((upload: Omit<UploadTask, 'progress' | 'status'>) => {
    const newUpload: UploadTask = {
      ...upload,
      progress: 0,
      status: 'uploading'
    };
    setUploads(prev => [...prev, newUpload]);
  }, []);

  const updateUpload = useCallback((id: string, updates: Partial<UploadTask>) => {
    setUploads(prev => 
      prev.map(upload => 
        upload.id === id ? { ...upload, ...updates } : upload
      )
    );
  }, []);

  const removeUpload = useCallback((id: string) => {
    setUploads(prev => prev.filter(upload => upload.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setUploads(prev => prev.filter(upload => 
      upload.status !== 'completed' && upload.status !== 'error'
    ));
  }, []);

  return (
    <UploadContext.Provider value={{
      uploads,
      addUpload,
      updateUpload,
      removeUpload,
      clearCompleted
    }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUploadContext() {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUploadContext must be used within an UploadProvider');
  }
  return context;
}